import { useCallback, useEffect, useState } from "react";
import { useUpdateRouteWithJobsMutation } from "~/shared/api/routes/route.grpc";
import { useDetachJobFromRouteMutation } from "~/shared/api/jobs/job.grpc";
import { useToast } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { isFinalJobStatus } from "~/shared/lib/job-status";

/**
 * Hook to manage route job selection and mutations
 */
export function useRouteJobs(routes: ConnectProto.Route[], onSuccess?: () => void) {
  const { toast } = useToast();
  const t = useT();
  const [routeSelectedMap, setRouteSelectedMap] = useState<Map<bigint, Set<bigint>>>(new Map());
  const [updateRouteWithJobs, { isLoading: isUpdatingRoute }] = useUpdateRouteWithJobsMutation();
  const [detachJobFromRoute, { isLoading: isDetaching }] = useDetachJobFromRouteMutation();
  const { activeCompany } = useAuthSession();
  const routesKey = routes.map((route) => route.id).join(",");

  useEffect(() => {
    setRouteSelectedMap(new Map());
  }, [routesKey]);

  /** Unticks the jobs an action already settled, keeping the rest for a retry. */
  const dropFromSelection = useCallback((routeId: bigint, settledKeys: ReadonlySet<string>) => {
    setRouteSelectedMap((prev) => {
      const kept = new Set(
        [...(prev.get(routeId) ?? [])].filter((id) => !settledKeys.has(String(id))),
      );
      return new Map(prev).set(routeId, kept);
    });
  }, []);

  const handleRemoveSelectedJobs = useCallback(
    async (route: ConnectProto.Route, selectedIds: Set<bigint>) => {
      // The RTK cache stores bigints as strings (normalizeBigInt), so ids are
      // compared as strings — Set<bigint>.has(cachedId) never matches.
      const selectedKeys = new Set(Array.from(selectedIds, String));
      // Final-status jobs are route history: the backend refuses to detach
      // them, the same way the route card disables the action.
      const jobsToDetach = route.jobs.filter(
        (job) => selectedKeys.has(String(job.id)) && !isFinalJobStatus(job.status),
      );

      if (jobsToDetach.length === 0) {
        toast({
          title: t(Namespace.Routes, "error"),
          description: t(Namespace.Routes, "selectJobToRemove"),
          variant: "destructive",
        });
        return;
      }

      const detachedKeys = new Set<string>();
      try {
        // Sequential on purpose: every detach replans the same route.
        for (const job of jobsToDetach) {
          await detachJobFromRoute({
            id: BigInt(job.id),
            serviceProviderId: BigInt(job.serviceProviderId),
            serviceReceiverId: BigInt(job.serviceReceiverId),
          }).unwrap();
          detachedKeys.add(String(job.id));
        }

        const routeEmptied =
          jobsToDetach.length === route.jobs.length &&
          route.status !== ConnectProto.RouteStatus.CREATED &&
          route.status !== ConnectProto.RouteStatus.NOT_APPROVED;
        toast({
          title: t(Namespace.Routes, "success"),
          description: routeEmptied
            ? t(Namespace.Routes, "routeCancelledAsEmpty")
            : t(Namespace.Routes, "jobsRemovedSuccess"),
        });

        setRouteSelectedMap((prev) => new Map(prev).set(route.id, new Set()));
        onSuccess?.();
      } catch (error) {
        // The failed RPC is already toasted by the global error toaster; only
        // the selection needs mending: jobs detached before the failure stayed
        // detached, and their ids must not linger in the selection where the
        // transfer action could resubmit them.
        console.error("Error removing jobs:", error);
        dropFromSelection(route.id, detachedKeys);
        if (detachedKeys.size > 0) onSuccess?.();
      }
    },
    [detachJobFromRoute, toast, t, onSuccess, dropFromSelection],
  );

  const handleRowSelectionChange = useCallback((routeId: bigint, selectedIds: Set<bigint>) => {
    setRouteSelectedMap((prev) => new Map(prev).set(routeId, selectedIds));
  }, []);

  const handleReorderJobs = useCallback(
    async (routeId: bigint, reorderedJobIds: bigint[]) => {
      try {
        await updateRouteWithJobs({
          route: { id: BigInt(routeId) },
          jobIds: reorderedJobIds.map(BigInt),
          ownerOrganizationId: BigInt(activeCompany?.id ?? 1),
        }).unwrap();
        onSuccess?.();
      } catch (error) {
        toast({
          title: "Ошибка",
          description: "Не удалось сохранить новый порядок заданий",
          variant: "destructive",
        });
        console.error("Error reordering jobs:", error);
      }
    },
    [updateRouteWithJobs, toast, activeCompany, onSuccess],
  );

  const clearRouteSelection = useCallback((routeId: bigint) => {
    setRouteSelectedMap((prev) => new Map(prev).set(routeId, new Set<bigint>()));
  }, []);

  const clearAllSelection = useCallback(() => {
    setRouteSelectedMap(new Map());
  }, []);

  return {
    routeSelectedMap,
    isUpdatingRoute: isUpdatingRoute || isDetaching,
    handleRemoveSelectedJobs,
    handleRowSelectionChange,
    handleReorderJobs,
    clearRouteSelection,
    clearAllSelection,
    dropFromSelection,
  };
}
